import React, { useMemo } from 'react';
import { Card, Row, Col, Badge, ProgressBar } from 'react-bootstrap';
import { FaShieldAlt, FaExclamationTriangle, FaChartLine, FaUsers, FaBuilding, FaUserCircle } from 'react-icons/fa';

const SecurityTenantView = ({ securityData, summaryData, tenant, onOrgSelect, onStandaloneSelect }) => {
  const { orgMetrics, standaloneMetrics } = useMemo(() => {
    const orgMap = {};
    const standalone = { critical: 0, high: 0, medium: 0, low: 0, total: 0, accounts: [] };
    
    const orgs = tenant?.cloudAccounts?.organizations || [];
    const standaloneAccounts = tenant?.cloudAccounts?.aws || [];
    const accountBreakdown = summaryData?.accountBreakdown || [];
    
    // Initialize org metrics
    orgs.forEach(org => {
      orgMap[org.orgId] = { critical: 0, high: 0, medium: 0, low: 0, total: 0, accounts: [] };
    });
    
    // Process account breakdown from summary API
    accountBreakdown.forEach(account => {
      const accountId = account.accountId;
      let foundInOrg = false;
      
      orgs.forEach(org => {
        const isManagement = org.id === accountId;
        const isMember = org.memberAccounts?.some(m => m.accountId === accountId);
        
        if (isManagement || isMember) {
          orgMap[org.orgId].critical += account.critical || 0;
          orgMap[org.orgId].high += account.high || 0;
          orgMap[org.orgId].medium += account.medium || 0;
          orgMap[org.orgId].low += account.low || 0;
          orgMap[org.orgId].total += account.totalFindings || 0;
          orgMap[org.orgId].accounts.push(account);
          foundInOrg = true;
        }
      });
      
      if (!foundInOrg && standaloneAccounts.some(acc => acc.accountId === accountId)) {
        standalone.critical += account.critical || 0;
        standalone.high += account.high || 0;
        standalone.medium += account.medium || 0;
        standalone.low += account.low || 0;
        standalone.total += account.totalFindings || 0;
        standalone.accounts.push(account);
      }
    });
    
    console.log('Org calculations:', { orgMap, standalone, accountBreakdown, orgs, standaloneAccounts });
    return { orgMetrics: orgMap, standaloneMetrics: standalone };
  }, [summaryData, tenant]);

  const organizations = tenant?.cloudAccounts?.organizations || [];
  const standaloneAccounts = tenant?.cloudAccounts?.aws || [];

  const getRiskScore = (critical, high, total) => {
    if (total === 0) return 0;
    return Math.round(((critical * 10 + high * 5) / total) * 10);
  };

  const getRiskLevel = (score) => {
    if (score >= 70) return { level: 'Critical', color: '#dc3545', bg: '#f8d7da' };
    if (score >= 40) return { level: 'High', color: '#fd7e14', bg: '#ffeaa7' };
    if (score >= 20) return { level: 'Medium', color: '#ffc107', bg: '#fff3cd' };
    return { level: 'Low', color: '#28a745', bg: '#d4edda' };
  };

  return (
    <div className="container">
      {/* CISO Executive Summary */}
      <div className="mb-4">
        <h4 style={{ color: '#1e293b', fontWeight: '600', marginBottom: '8px' }}>Security Posture Overview</h4>
        <p style={{ fontSize: '0.875rem', color: '#64748b' }}>Executive security metrics across all cloud environments</p>
      </div>

      <Row className="mb-4">
        <Col md={3}>
          <Card style={{ background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)', color: 'white', borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(220, 53, 69, 0.2)' }}>
            <Card.Body className="text-center">
              <div style={{ fontSize: '2rem', marginBottom: '8px' }}><FaExclamationTriangle /></div>
              <h3 className="mb-0">{summaryData?.criticalFindings || 0}</h3>
              <small style={{ opacity: 0.9 }}>Critical Findings</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card style={{ background: 'linear-gradient(135deg, #fd7e14 0%, #e8590c 100%)', color: 'white', borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(253, 126, 20, 0.2)' }}>
            <Card.Body className="text-center">
              <div style={{ fontSize: '2rem', marginBottom: '8px' }}><FaShieldAlt /></div>
              <h3 className="mb-0">{summaryData?.highFindings || 0}</h3>
              <small style={{ opacity: 0.9 }}>High Risk</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card style={{ background: 'linear-gradient(135deg, #6D5BFF 0%, #5a4ad6 100%)', color: 'white', borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(109, 91, 255, 0.2)' }}>
            <Card.Body className="text-center">
              <div style={{ fontSize: '2rem', marginBottom: '8px' }}><FaChartLine /></div>
              <h3 className="mb-0">{summaryData?.totalFindings || 0}</h3>
              <small style={{ opacity: 0.9 }}>Total Findings</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card style={{ background: 'linear-gradient(135deg, #28a745 0%, #1e7e34 100%)', color: 'white', borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(40, 167, 69, 0.2)' }}>
            <Card.Body className="text-center">
              <div style={{ fontSize: '2rem', marginBottom: '8px' }}><FaUsers /></div>
              <h3 className="mb-0">{summaryData?.accountsScanned || 0}</h3>
              <small style={{ opacity: 0.9 }}>Accounts Scanned</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Risk Distribution */}
      <Row className="mb-4">
        <Col>
          <Card style={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <Card.Body>
              <h6 style={{ color: '#1e293b', fontWeight: '600', marginBottom: '16px' }}>Risk Distribution</h6>
              <div className="mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <span style={{ fontSize: '0.875rem', color: '#64748b' }}>Critical</span>
                  <span style={{ fontSize: '0.875rem', fontWeight: '600' }}>{summaryData?.criticalFindings || 0}</span>
                </div>
                <ProgressBar 
                  variant="danger" 
                  now={summaryData?.totalFindings > 0 ? (summaryData.criticalFindings / summaryData.totalFindings) * 100 : 0} 
                  style={{ height: '8px' }}
                />
              </div>
              <div className="mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <span style={{ fontSize: '0.875rem', color: '#64748b' }}>High</span>
                  <span style={{ fontSize: '0.875rem', fontWeight: '600' }}>{summaryData?.highFindings || 0}</span>
                </div>
                <ProgressBar 
                  variant="warning" 
                  now={summaryData?.totalFindings > 0 ? (summaryData.highFindings / summaryData.totalFindings) * 100 : 0} 
                  style={{ height: '8px' }}
                />
              </div>
              <div className="mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <span style={{ fontSize: '0.875rem', color: '#64748b' }}>Medium</span>
                  <span style={{ fontSize: '0.875rem', fontWeight: '600' }}>{summaryData?.mediumFindings || 0}</span>
                </div>
                <ProgressBar 
                  variant="info" 
                  now={summaryData?.totalFindings > 0 ? (summaryData.mediumFindings / summaryData.totalFindings) * 100 : 0} 
                  style={{ height: '8px' }}
                />
              </div>
              <div>
                <div className="d-flex justify-content-between mb-1">
                  <span style={{ fontSize: '0.875rem', color: '#64748b' }}>Low</span>
                  <span style={{ fontSize: '0.875rem', fontWeight: '600' }}>{summaryData?.lowFindings || 0}</span>
                </div>
                <ProgressBar 
                  variant="success" 
                  now={summaryData?.totalFindings > 0 ? (summaryData.lowFindings / summaryData.totalFindings) * 100 : 0} 
                  style={{ height: '8px' }}
                />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Organizations & Accounts */}
      <div className="mb-3">
        <h5 style={{ color: '#1e293b', fontWeight: '600' }}>Security by Organization & Account</h5>
        <p style={{ fontSize: '0.875rem', color: '#64748b' }}>Drill down into specific organizations or standalone accounts for detailed security analysis</p>
      </div>

      <Row>
        {standaloneAccounts.length > 0 && (
          <Col md={6} className="mb-3">
            <Card 
              onClick={onStandaloneSelect} 
              style={{ 
                borderRadius: '12px', 
                border: '1px solid #e2e8f0', 
                cursor: 'pointer', 
                transition: 'all 0.2s', 
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)' 
              }}
              className="h-100"
            >
              <Card.Body>
                <div className="d-flex align-items-center mb-3">
                  <div style={{ 
                    width: '48px', 
                    height: '48px', 
                    borderRadius: '50%', 
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    fontSize: '1.5rem', 
                    marginRight: '12px',
                    color: 'white'
                  }}>
                    <FaUserCircle />
                  </div>
                  <div>
                    <h6 className="mb-0" style={{ fontSize: '1rem', color: '#1e293b' }}>Standalone Accounts</h6>
                    <small style={{ color: '#64748b', fontSize: '0.75rem' }}>Individual AWS accounts</small>
                  </div>
                  <div className="ms-auto">
                    {(() => {
                      const riskScore = getRiskScore(standaloneMetrics.critical, standaloneMetrics.high, standaloneMetrics.total);
                      const risk = getRiskLevel(riskScore);
                      return (
                        <Badge style={{ backgroundColor: risk.color, fontSize: '0.75rem' }}>
                          {risk.level}
                        </Badge>
                      );
                    })()}
                  </div>
                </div>
                
                <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '12px', marginBottom: '12px' }}>
                  <Row>
                    <Col xs={6}>
                      <div className="text-center">
                        <div style={{ fontSize: '1.5rem', fontWeight: '600', color: '#dc3545' }}>{standaloneMetrics.critical}</div>
                        <small style={{ color: '#64748b' }}>Critical</small>
                      </div>
                    </Col>
                    <Col xs={6}>
                      <div className="text-center">
                        <div style={{ fontSize: '1.5rem', fontWeight: '600', color: '#fd7e14' }}>{standaloneMetrics.high}</div>
                        <small style={{ color: '#64748b' }}>High</small>
                      </div>
                    </Col>
                  </Row>
                  <hr style={{ margin: '8px 0' }} />
                  <div className="d-flex justify-content-between">
                    <span style={{ fontSize: '0.875rem', color: '#64748b' }}>Total Findings</span>
                    <strong style={{ color: '#1e293b' }}>{standaloneMetrics.total}</strong>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span style={{ fontSize: '0.875rem', color: '#64748b' }}>Accounts</span>
                    <strong style={{ color: '#1e293b' }}>{standaloneMetrics.accounts.length}</strong>
                  </div>
                </div>
                
                <div className="text-center" style={{ fontSize: '0.875rem', color: '#6D5BFF' }}>
                  Click to analyze security details →
                </div>
              </Card.Body>
            </Card>
          </Col>
        )}

        {organizations.map((org) => {
          const metrics = orgMetrics[org.orgId] || { critical: 0, high: 0, medium: 0, low: 0, total: 0, accounts: new Set() };
          const riskScore = getRiskScore(metrics.critical, metrics.high, metrics.total);
          const risk = getRiskLevel(riskScore);
          
          return (
            <Col md={6} key={org.orgId} className="mb-3">
              <Card 
                onClick={() => onOrgSelect(org.orgId)} 
                style={{ 
                  borderRadius: '12px', 
                  border: '1px solid #e2e8f0', 
                  cursor: 'pointer', 
                  transition: 'all 0.2s', 
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)' 
                }}
                className="h-100"
              >
                <Card.Body>
                  <div className="d-flex align-items-center mb-3">
                    <div style={{ 
                      width: '48px', 
                      height: '48px', 
                      borderRadius: '50%', 
                      background: 'linear-gradient(135deg, #6D5BFF 0%, #5a4ad6 100%)', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      fontSize: '1.5rem', 
                      marginRight: '12px',
                      color: 'white'
                    }}>
                      <FaBuilding />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h6 className="mb-0" style={{ 
                        fontSize: '0.875rem', 
                        whiteSpace: 'nowrap', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        color: '#1e293b'
                      }}>
                        {org.alias || 'AWS Organization'}
                      </h6>
                      <small style={{ color: '#64748b', fontSize: '0.75rem' }}>{org.orgId}</small>
                    </div>
                    <div>
                      <Badge style={{ backgroundColor: risk.color, fontSize: '0.75rem' }}>
                        {risk.level}
                      </Badge>
                    </div>
                  </div>
                  
                  <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '12px', marginBottom: '12px' }}>
                    <Row>
                      <Col xs={6}>
                        <div className="text-center">
                          <div style={{ fontSize: '1.5rem', fontWeight: '600', color: '#dc3545' }}>{metrics.critical}</div>
                          <small style={{ color: '#64748b' }}>Critical</small>
                        </div>
                      </Col>
                      <Col xs={6}>
                        <div className="text-center">
                          <div style={{ fontSize: '1.5rem', fontWeight: '600', color: '#fd7e14' }}>{metrics.high}</div>
                          <small style={{ color: '#64748b' }}>High</small>
                        </div>
                      </Col>
                    </Row>
                    <hr style={{ margin: '8px 0' }} />
                    <div className="d-flex justify-content-between">
                      <span style={{ fontSize: '0.875rem', color: '#64748b' }}>Total Findings</span>
                      <strong style={{ color: '#1e293b' }}>{metrics.total}</strong>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span style={{ fontSize: '0.875rem', color: '#64748b' }}>Accounts</span>
                      <strong style={{ color: '#1e293b' }}>{metrics.accounts.length}</strong>
                    </div>
                  </div>
                  
                  <div className="text-center" style={{ fontSize: '0.875rem', color: '#6D5BFF' }}>
                    Click to analyze security details →
                  </div>
                </Card.Body>
              </Card>
            </Col>
          );
        })}
      </Row>
    </div>
  );
};

export default SecurityTenantView;